'use client';

/**
 * WishlistButton — ♡ toggle button for saving/unsaving a product.
 *
 * Spec ref: SPEC-148 (Sprint 72, 2026-09-07) — F-004a: Wishlist Market.
 *
 * Calls POST /api/market/wishlist/:productId (toggle).
 * Shows filled heart if saved, outline heart if not.
 * Best-effort — if not logged in, the button is hidden.
 *
 * Anti-§6: pure UI toggle — no business logic.
 */

import { useState } from 'react';
import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { apiFetch, ApiClientError } from '@/lib/api-client';

export function WishlistButton({
  productId,
  initiallySaved = false,
}: {
  productId: string;
  initiallySaved?: boolean;
}) {
  const [saved, setSaved] = useState(initiallySaved);
  const [loading, setLoading] = useState(false);

  async function handleToggle() {
    setLoading(true);
    try {
      const res = await apiFetch<{ data: { saved: boolean } }>(
        `/market/wishlist/${productId}`,
        { method: 'POST', rawResponse: true },
      );
      setSaved(res.data.saved);
    } catch (err) {
      // Silent fail — if not logged in (401) or other error, just don't toggle
      if (err instanceof ApiClientError && err.status === 401) {
        // Not logged in — hide button (or could redirect to /login)
        return;
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-8 absolute top-2 right-2 z-10 bg-background/80 backdrop-blur hover:bg-background"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleToggle();
      }}
      disabled={loading}
      aria-label={saved ? 'Quitar de wishlist' : 'Guardar en wishlist'}
    >
      <Heart
        className={cn(
          'size-4 transition-colors',
          saved ? 'fill-red-500 text-red-500' : 'text-muted-foreground',
        )}
        aria-hidden="true"
      />
    </Button>
  );
}
